import React, { useState } from 'react'

import {
    Card,
    CardContent,
    IconButton,
    Typography,
    Box,
    Divider,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import SaveIcon from '@material-ui/icons/Save'
import CancelIcon from '@material-ui/icons/Cancel'

// Accept property info as props
interface InvoicePropertyDetailsProps {
    name: string
    billableUnits: number
    ratePerUnit: number
    onUpdateBilling?: (
        billableUnits: number,
        ratePerUnit: number,
    ) => Promise<void>
    // paymentTerms will be static for now
}

export const InvoicePropertyDetails: React.FC<InvoicePropertyDetailsProps> = ({
    name,
    billableUnits,
    ratePerUnit,
    onUpdateBilling,
}) => {
    const [isEditing, setIsEditing] = useState(false)
    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [editBillableUnits, setEditBillableUnits] = useState(
        billableUnits.toString(),
    )
    const [editRatePerUnit, setEditRatePerUnit] = useState(
        ratePerUnit.toString(),
    )
    const [isUpdating, setIsUpdating] = useState(false)

    const handleEditClick = () => {
        setEditBillableUnits(billableUnits.toString())
        setEditRatePerUnit(ratePerUnit.toString())
        setEditDialogOpen(true)
    }

    const handleSave = async () => {
        const newBillableUnits = parseInt(editBillableUnits)
        const newRatePerUnit = parseFloat(editRatePerUnit)

        if (isNaN(newBillableUnits) || newBillableUnits < 0) {
            alert('Please enter a valid number for billable units')
            return
        }

        if (isNaN(newRatePerUnit) || newRatePerUnit < 0) {
            alert('Please enter a valid rate per unit')
            return
        }

        if (onUpdateBilling) {
            setIsUpdating(true)
            try {
                await onUpdateBilling(newBillableUnits, newRatePerUnit)
                setEditDialogOpen(false)
            } catch (error) {
                console.error('Failed to update billing:', error)
                alert('Failed to update billing configuration')
            } finally {
                setIsUpdating(false)
            }
        }
    }

    const handleCancel = () => {
        setEditDialogOpen(false)
        setEditBillableUnits(billableUnits.toString())
        setEditRatePerUnit(ratePerUnit.toString())
    }

    return (
        <Box display="flex" flexDirection="column">
            {/* Header */}
            <Box>
                <Typography variant="h6">{name}</Typography>
            </Box>
            <Card elevation={2} style={{ marginBottom: 16, maxWidth: 700 }}>
                <CardContent style={{ padding: 16 }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Box>
                            <Typography
                                variant="h6"
                                style={{ fontWeight: 600 }}
                            >
                                Internal Invoicing
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            style={{ marginLeft: 8 }}
                            onClick={handleEditClick}
                            title="Edit billing configuration"
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Divider style={{ margin: '8px 0' }} />
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Box mr={3} mb={1}>
                            <Typography variant="body2" color="textSecondary">
                                Billable Units
                            </Typography>
                            <Typography variant="subtitle1">
                                {billableUnits}
                            </Typography>
                        </Box>
                        <Box mr={3} mb={1}>
                            <Typography variant="body2" color="textSecondary">
                                Rate per Unit
                            </Typography>
                            <Typography variant="subtitle1">
                                ${ratePerUnit.toFixed(2)}
                            </Typography>
                        </Box>
                        <Box mb={1}>
                            <Typography variant="body2" color="textSecondary">
                                Payment Terms
                            </Typography>
                            <Typography variant="subtitle1">Net 30</Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Edit Billing Configuration Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={handleCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Edit Billing Configuration</DialogTitle>
                <DialogContent>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginBottom: 16 }}
                    >
                        Update the billing configuration for {name}
                    </Typography>
                    <TextField
                        fullWidth
                        label="Billable Units"
                        type="number"
                        value={editBillableUnits}
                        onChange={(e) => setEditBillableUnits(e.target.value)}
                        variant="outlined"
                        margin="normal"
                        helperText="Number of billable units"
                    />
                    <TextField
                        fullWidth
                        label="Rate per Unit"
                        type="number"
                        value={editRatePerUnit}
                        onChange={(e) => setEditRatePerUnit(e.target.value)}
                        variant="outlined"
                        margin="normal"
                        helperText="Rate per billable unit"
                        InputProps={{
                            startAdornment: '$',
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCancel}
                        color="primary"
                        startIcon={<CancelIcon />}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        disabled={isUpdating}
                        startIcon={<SaveIcon />}
                    >
                        {isUpdating ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
